import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Slide,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';

// Transition components
const SlideTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const FadeTransition = React.forwardRef<
  unknown,
  TransitionProps & { children: React.ReactElement }
>((props, ref) => <Fade ref={ref} {...props} />);

export interface ModalProps {
  isOpen?: boolean;
  open?: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  transition?: 'slide' | 'fade';
  closeOnBackdrop?: boolean;
  disableEscapeKeyDown?: boolean;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  open,
  onClose,
  title,
  children,
  actions,
  size = 'md',
  fullScreen,
  fullWidth = true,
  showCloseButton = true,
  transition = 'fade',
  closeOnBackdrop = true,
  disableEscapeKeyDown = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdrop) {
      onClose();
    }
    event.stopPropagation();
  };

  // Get transition component
  const TransitionComponent =
    transition === 'slide' ? SlideTransition : FadeTransition;

  // Determine if should show full screen on mobile
  const shouldShowFullScreen = fullScreen || isMobile;

  const modalOpen = isOpen ?? open ?? false;

  // Map custom size names to MUI sizes
  const getMuiSize = (size: string) => {
    switch (size) {
      case 'small':
        return 'sm';
      case 'medium':
        return 'md';
      case 'large':
        return 'lg';
      default:
        return size as 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    }
  };

  return (
    <Dialog
      open={modalOpen}
      onClose={disableEscapeKeyDown ? undefined : onClose}
      maxWidth={getMuiSize(size)}
      fullWidth={fullWidth}
      fullScreen={shouldShowFullScreen}
      TransitionComponent={TransitionComponent}
      onClick={handleBackdropClick}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: shouldShowFullScreen ? 0 : 4,
          margin: shouldShowFullScreen ? 0 : 2,
          maxHeight: shouldShowFullScreen ? '100%' : 'calc(100% - 64px)',
        },
      }}
    >
      {/* Dialog Title with Close Button */}
      {title && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>{title}</Box>
          {showCloseButton && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                ml: 1,
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
      )}

      {/* Dialog Content */}
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        sx={{
          px: 3,
          py: title ? 3 : 4,
          overflow: 'auto',
        }}
      >
        {children}
      </DialogContent>

      {/* Dialog Actions */}
      {actions && (
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
